import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

/**
 * Hook for fetching and managing properties from group markers
 * @param {Object} options - Configuration options
 * @param {number} [options.cityId] - Optional city ID filter
 * @param {number} [options.communityId] - Optional community ID filter
 * @param {boolean} [options.autoFetch=true] - Whether to fetch data automatically on mount
 * @returns {Object} Group properties data and functions
 */
const useGroupProperties = ({ cityId, communityId, autoFetch = true } = {}) => {
  const [groupProperties, setGroupProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch group properties data
  const fetchGroupProperties = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (cityId) params.city_id = cityId;
      if (communityId) params.community_id = communityId;
      
      console.log("Fetching group properties with params:", params);

      const response = await api.get('/group-properties/', { params });
      console.log("Group Properties API response:", response.data);
      setGroupProperties(response.data);
    } catch (err) {
      console.error('Error fetching group properties:', err);
      setError(err.message || 'Failed to fetch group properties');
    } finally {
      setLoading(false);
    }
  }, [cityId, communityId]);

  // Get a single group property by ID
  const getGroupProperty = useCallback(async (propertyId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/group-properties/${propertyId}`);
      return response.data;
    } catch (err) {
      console.error(`Error fetching group property ${propertyId}:`, err);
      setError(err.message || 'Failed to fetch group property');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an existing group property
  const updateGroupProperty = useCallback(async (propertyId, propertyData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put(`/group-properties/${propertyId}`, propertyData);
      setGroupProperties((prevProperties) =>
        prevProperties.map((prop) =>
          prop.id === propertyId ? response.data : prop
        )
      );
      return response.data;
    } catch (err) {
      console.error(`Error updating group property ${propertyId}:`, err);
      setError(err.message || 'Failed to update group property');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a group property
  const deleteGroupProperty = useCallback(async (propertyId) => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/group-properties/${propertyId}`);
      setGroupProperties((prevProperties) =>
        prevProperties.filter((prop) => prop.id !== propertyId)
      );
      return true;
    } catch (err) {
      console.error(`Error deleting group property ${propertyId}:`, err);
      setError(err.message || 'Failed to delete group property');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on mount if autoFetch is true
  useEffect(() => {
    if (autoFetch) {
      fetchGroupProperties();
    }
  }, [autoFetch, fetchGroupProperties]);

  return {
    groupProperties,
    loading,
    error,
    fetchGroupProperties,
    getGroupProperty,
    updateGroupProperty,
    deleteGroupProperty,
  };
};

export default useGroupProperties; 